import type {
  B2BRegistrationErrorBody,
  B2BRegistrationStructuredErrorItem,
  B2BRegistrationValidationMessageItem,
} from 'api/responseTypes';

/** Form / API field names used for POST /consumers/b2b/register (excludes client-only `termsAccepted`). */
export const B2B_REGISTRATION_SUBMIT_FIELD_KEYS = [
  'fullName',
  'companyOrOrganization',
  'phoneNumber',
  'email',
  'city',
  'websiteOrSocialMedia',
  'hasOrganizedBefore',
  'previousEvents',
  'eventName',
  'description',
  'eventDate',
  'eventTime',
  'eventVenue',
  'isVenueConfirmed',
  'eventType',
  'publicEventCategory',
] as const;

export type B2BRegistrationSubmitFieldKey = (typeof B2B_REGISTRATION_SUBMIT_FIELD_KEYS)[number];

const SUBMIT_FIELD_KEY_SET = new Set<string>(B2B_REGISTRATION_SUBMIT_FIELD_KEYS);

const SUBMIT_FIELD_KEYS_BY_LENGTH = [...B2B_REGISTRATION_SUBMIT_FIELD_KEYS].sort(
  (a, b) => b.length - a.length,
);

/** RHF `setError` message id — copy is generic; avoids surfacing raw English API strings on fields. */
export const B2B_REGISTRATION_API_FIELD_ERROR_MESSAGE_ID = 'b2b.registration.field.invalid';

const pickFieldFromPath = (path: string | undefined): string | undefined => {
  if (path == null || path === '') {
    return undefined;
  }
  const head = path.replace(/^\//, '').split(/[/.[\]]/)[0];
  return head !== '' && SUBMIT_FIELD_KEY_SET.has(head) ? head : undefined;
};

const pushConstraintMessage = (
  item: B2BRegistrationValidationMessageItem,
  out: { field?: string; text: string }[],
): void => {
  const field = item.property;
  const constraints = item.constraints;
  const msg =
    constraints != null && typeof constraints === 'object'
      ? Object.values(constraints).find((v) => typeof v === 'string')
      : undefined;
  if (typeof msg === 'string') {
    out.push({ field, text: msg });
  } else if (field != null && SUBMIT_FIELD_KEY_SET.has(field)) {
    out.push({ field, text: field });
  }
};

const matchFieldFromFreeText = (text: string): B2BRegistrationSubmitFieldKey | undefined => {
  const lower = text.toLowerCase();
  for (const key of SUBMIT_FIELD_KEYS_BY_LENGTH) {
    if (lower.includes(key.toLowerCase())) {
      return key;
    }
  }
  return undefined;
};

const structuredEntries = (
  data: B2BRegistrationErrorBody,
): B2BRegistrationStructuredErrorItem[] => {
  const { errors } = data;
  return Array.isArray(errors) ? errors : [];
};

/**
 * Parses B2B register 400 response bodies into distinct form field keys (when possible).
 * Supports Nest string arrays, class-validator `message` objects, and simple `errors[]` with paths.
 */
export const extractB2bRegistrationSubmitFieldKeysFromErrorBody = (
  body: unknown,
): B2BRegistrationSubmitFieldKey[] => {
  if (body == null || typeof body !== 'object') {
    return [];
  }
  const data = body as B2BRegistrationErrorBody;
  const hinted: { field?: string; text: string }[] = [];

  for (const entry of structuredEntries(data)) {
    const pathField = pickFieldFromPath(typeof entry.path === 'string' ? entry.path : undefined);
    const explicit =
      typeof entry.field === 'string' && SUBMIT_FIELD_KEY_SET.has(entry.field)
        ? entry.field
        : undefined;
    const msg =
      typeof entry.message === 'string'
        ? entry.message
        : typeof entry.msg === 'string'
        ? entry.msg
        : '';
    if (explicit != null) {
      hinted.push({ field: explicit, text: msg });
    } else if (pathField != null) {
      hinted.push({ field: pathField, text: msg });
    } else if (msg !== '') {
      hinted.push({ text: msg });
    }
  }

  const rawMessage = data.message;
  if (Array.isArray(rawMessage)) {
    for (const item of rawMessage) {
      if (typeof item === 'string') {
        hinted.push({ text: item });
      } else if (item != null && typeof item === 'object' && 'property' in item) {
        pushConstraintMessage(item as B2BRegistrationValidationMessageItem, hinted);
      }
    }
  } else if (typeof rawMessage === 'string') {
    hinted.push({ text: rawMessage });
  }

  const keys = new Set<B2BRegistrationSubmitFieldKey>();
  for (const h of hinted) {
    let key =
      h.field != null && SUBMIT_FIELD_KEY_SET.has(h.field)
        ? (h.field as B2BRegistrationSubmitFieldKey)
        : undefined;
    if (key == null) {
      key = matchFieldFromFreeText(h.text);
    }
    if (key != null) {
      keys.add(key);
    }
  }
  return [...keys];
};
